import React, { useState, useEffect, useCallback } from 'react';
import { Page, Layout, Card, DataTable, Badge, Button, Modal, Select, InlineStack } from '@shopify/polaris';
import { reviewService } from '../services/reviewService';
import { openprojectService } from '../services/openprojectService';
import LoadingState from './shared/LoadingState';
import PageError from './shared/PageError';
import ReviewDetail from './shared/ReviewDetail';

const statusTone = { pending: 'warning', approved: 'success', rejected: 'critical' };

const periodLabel = (review) =>
  new Date(review.year, review.month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

// Admin-facing: every review, across every person and manager. Admin can
// open and act on any review that's still pending.
const MonthlyReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [peopleById, setPeopleById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeReviewId, setActiveReviewId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async (status) => {
    try {
      setLoading(true);
      setError('');
      const [reviewData, users] = await Promise.all([reviewService.getAll(status), openprojectService.getUsers()]);
      setReviews(reviewData.reviews || []);
      setPeopleById(Object.fromEntries(users.map((u) => [u.id.toString(), u.name])));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(statusFilter || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  if (loading && reviews.length === 0) {
    return (
      <Page title="Monthly Reviews">
        <LoadingState />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Monthly Reviews" primaryAction={{ content: 'Retry', onAction: () => load(statusFilter || undefined) }}>
        <PageError>{error}</PageError>
      </Page>
    );
  }

  const rows = reviews.map((review) => [
    peopleById[review.openProjectUserId] || review.openProjectUserId,
    peopleById[review.assignedManagerId] || (review.assignedManagerId ? review.assignedManagerId : 'Global Admin'),
    periodLabel(review),
    <Badge tone={statusTone[review.status]}>{review.status}</Badge>,
    <Button size="slim" onClick={() => setActiveReviewId(review._id)}>
      {review.status === 'pending' ? 'Review' : 'View'}
    </Button>
  ]);

  return (
    <Page title="Monthly Reviews" subtitle="Every person's monthly review, across all managers">
      <Layout>
        <Layout.Section>
          <Card>
            <InlineStack gap="300" blockAlign="center">
              <div style={{ minWidth: 200 }}>
                <Select
                  label="Status"
                  labelHidden
                  options={[
                    { label: 'All statuses', value: '' },
                    { label: 'Pending', value: 'pending' },
                    { label: 'Approved', value: 'approved' },
                    { label: 'Rejected', value: 'rejected' }
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>
        <Layout.Section>
          <Card>
            {reviews.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem' }}>No reviews found.</p>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                headings={['Person', 'Manager', 'Period', 'Status', 'Actions']}
                rows={rows}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <Modal open={!!activeReviewId} onClose={() => setActiveReviewId(null)} title="Monthly Time Review" large>
        <Modal.Section>
          {activeReviewId && (
            <ReviewDetail
              reviewId={activeReviewId}
              onDecided={() => {
                load(statusFilter || undefined);
              }}
            />
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default MonthlyReviews;
